import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { labelStyle, inputStyle } from './adminFieldStyles';

const ACCEPTED = 'image/jpeg,image/jpg,image/png,image/webp,image/gif';
const MAX_BYTES = 5 * 1024 * 1024;

const ImageUploadField = ({
  label,
  value,
  onChange,
  bucket = 'products_images',
  onUploadingChange,
  hideUrlInput = false,
}) => {
  const { t } = useTranslation('common');
  const fileRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [justUploaded, setJustUploaded] = useState(false);

  useEffect(() => {
    onUploadingChange?.(isUploading);
  }, [isUploading, onUploadingChange]);

  // Unmount-only safety net: if this field is removed mid-upload (e.g. its
  // entry is deleted from a repeatable list — see CustomShowHistoryField),
  // report upload-done so the parent's uploading tracker doesn't get stuck
  // forever. A ref (rather than adding to the effect above) avoids re-firing
  // this on every isUploading/onUploadingChange change — it should only run
  // once, on unmount.
  const isUploadingRef = useRef(isUploading);
  isUploadingRef.current = isUploading;
  const onUploadingChangeRef = useRef(onUploadingChange);
  onUploadingChangeRef.current = onUploadingChange;
  useEffect(() => {
    return () => {
      if (isUploadingRef.current) onUploadingChangeRef.current?.(false);
    };
  }, []);

  const handleUpload = async (file) => {
    setError(null);

    if (file.size > MAX_BYTES) {
      setError(t('uploads.image.sizeError'));
      return;
    }

    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const path = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;

    setIsUploading(true);
    try {
      const { error: uploadErr } = await supabase.storage
        .from(bucket)
        .upload(path, file, { cacheControl: '3600', upsert: false });

      if (uploadErr) {
        setError(t('uploads.uploadError', { detail: uploadErr.message }));
        return;
      }

      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      onChange(data.publicUrl);
      setJustUploaded(true);
    } catch (err) {
      setError(t('uploads.unexpectedError', { detail: err.message }));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <label style={labelStyle}>{label}</label>

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem' }}>
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={isUploading}
          style={{
            padding: '0.45rem 1rem',
            background: isUploading ? '#f0f0f0' : '#f8f9fa',
            border: '1px solid #ddd',
            borderRadius: '8px',
            fontSize: '0.85rem',
            fontWeight: '600',
            color: isUploading ? '#aaa' : '#555',
            cursor: isUploading ? 'default' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {isUploading ? t('uploads.uploading') : t('uploads.image.upload')}
        </button>
        <span style={{ fontSize: '0.8rem', color: '#aaa' }}>{t('uploads.image.hint')}</span>
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPTED}
          style={{ display: 'none' }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleUpload(file);
            e.target.value = '';
          }}
        />
      </div>

      {!hideUrlInput && (
        <input
          type="url"
          value={value}
          onChange={(e) => { onChange(e.target.value); setJustUploaded(false); }}
          placeholder={t('uploads.urlPlaceholder')}
          style={{ ...inputStyle, borderColor: isUploading ? '#2ca9e1' : '#ddd' }}
        />
      )}

      {error && (
        <p style={{ color: '#dc3545', fontSize: '0.82rem', marginTop: '0.4rem', marginBottom: 0 }}>
          {error}
        </p>
      )}

      {value && (
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <img
            src={value}
            alt={t('uploads.previewAlt')}
            style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: '1px solid #eee', flexShrink: 0 }}
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          {justUploaded && (
            <span style={{ color: '#28a745', fontSize: '0.82rem', fontWeight: '500' }}>
              {t('uploads.image.success')}
            </span>
          )}
          <button
            type="button"
            onClick={() => { onChange(''); setJustUploaded(false); }}
            style={{
              background: 'none',
              border: 'none',
              color: '#dc3545',
              fontSize: '0.82rem',
              cursor: 'pointer',
              padding: 0,
              marginLeft: 'auto',
            }}
          >
            {t('uploads.image.remove')}
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageUploadField;
